import { IconButton, Spinner } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import type { TrainingEvalRunSummaryView } from "@/api/trainingEval";
import { EmptyState } from "@/shared/ui/emptyState/public";
import {
  inlineLoadingLineClassName,
  inlineSpinnerClassName,
} from "@/shared/ui/loadingState/public";
import { IsaacEvalComparison } from "./IsaacEvalComparison";
import { CompareSummaryTable } from "./CompareSummaryTable";
import { DistributionCompareChart } from "./DistributionCompareChart";
import { DurationCompareChart } from "./DurationCompareChart";
import { KpiCompareChart } from "./KpiCompareChart";
import { OutcomeCompareChart } from "./OutcomeCompareChart";
import { PolicyCompareTable } from "./PolicyCompareTable";
import { ScenarioCompareTable } from "./ScenarioCompareTable";
import { SeedStabilityTable } from "./SeedStabilityTable";
import { SeriesSwatch } from "./SeriesSwatch";
import { openTrainingSessionWindow, sessionReference } from "../trainingRefs";
import type { EvalResultSeries } from "./evalCompareModel";
import type { EvalCompareGroup } from "./policyCompareGroups";
import type { EvalCompareController } from "./useEvalCompareController";
import {
  useEvaluationComparisonViewModel,
  type EvaluationComparisonMode,
} from "./useEvaluationComparisonViewModel";
import styles from "./EvaluationComparisonViewer.module.css";

export function EvaluationComparisonViewer({
  controller,
  onOpenRunDetail,
  mode = "compare",
  groups,
}: {
  controller: EvalCompareController;
  onOpenRunDetail: (run: TrainingEvalRunSummaryView) => void;
  mode?: EvaluationComparisonMode;
  groups?: readonly EvalCompareGroup[];
}): ReactNode {
  const view = useEvaluationComparisonViewModel({
    controller,
    onOpenRunDetail,
    mode,
    groups,
  });
  const {
    allPoliciesEmpty,
    baselineSeries,
    baselineLoading,
    compareRuns,
    configurationRuns,
    config,
    exportAppendix,
    exportFilename,
    groupLegendItems,
    hasDistributions,
    hasHeadlineMetrics,
    hasOutcomes,
    hasScenarioData,
    loadFailed,
    loading,
    mergedResult,
    notices,
    openRunByKey,
    progressText,
    resultSeries,
    retry,
    showMergedResults,
    summarySeries,
    termsFailed,
  } = view;

  return (
    <article className={styles.root}>
      <header className={styles.header}>
        <h2 className={styles.title}>{config.title}</h2>
        <span className={styles.subtitle}>
          {progressText}
          {mergedResult !== null
            ? ` · 고유 Seed ${mergedResult.uniqueSeedCount}개 · 통합 Episode ${mergedResult.episodeCount}개`
            : ""}
          {loading ? <SubtitleLoading>상세를 불러오는 중…</SubtitleLoading> : null}
          {baselineLoading ? <SubtitleLoading>Baseline을 불러오는 중…</SubtitleLoading> : null}
        </span>
      </header>

      <div className={styles.sections}>
        <div className={styles.legend}>
          {baselineSeries !== null ? (
            <LegendChip
              item={baselineSeries}
              onOpen={openBaselineSession(baselineSeries.baseline)}
              openTitle="Baseline 을 만든 schedule session 창을 엽니다."
            />
          ) : null}
          {groupLegendItems !== null
            ? groupLegendItems.map((item) => <LegendChip key={item.key} item={item} />)
            : compareRuns.map((item) => (
                <LegendChip
                  key={item.key}
                  item={item}
                  onOpen={() => onOpenRunDetail(item.run.summaryRow)}
                />
              ))}
        </div>

        {/* 비교할 실행이 하나도 없으면 이 안내가 창 본문의 빈 상태다. 그래서 안내 상자의 한 줄로 두지 않는다. */}
        {allPoliciesEmpty ? (
          <EmptyState>
            선택한 Policy 에 성공한 Evaluation 실행이 없어 비교할 결과가 없습니다. Policy 목록에서
            Evaluation을 먼저 실행해 주세요.
          </EmptyState>
        ) : null}

        {loadFailed || notices.length > 0 ? (
          <div className={`${styles.noticeBox} ${loadFailed ? styles.noticeBoxError : ""}`}>
            {loadFailed ? (
              <div className={`${styles.noticeRow} ${styles.noticeError}`} role="alert">
                <span>
                  {config.resultsNeedPooling && termsFailed
                    ? `일부 실행의 success term 을 불러오지 못해 ${config.pooledNoun}에서 뺐습니다. 다시 시도하면 포함됩니다.`
                    : "일부 실행의 상세를 불러오지 못했습니다. 비어 있는 값은 N/A 로 표시합니다."}
                </span>
                <IconButton
                  type="button"
                  variant="surface"
                  size="sm"
                  text="다시 시도"
                  onClick={retry}
                />
              </div>
            ) : null}
            {notices.map((notice) => (
              <div
                key={notice.key}
                className={`${styles.noticeRow} ${
                  notice.tone === "info" ? styles.noticeInfo : styles.noticeWarning
                }`}
                role="status"
              >
                <div className={styles.noticeStack}>
                  <span>{notice.message}</span>
                  {notice.items !== undefined && notice.items.length > 0 ? (
                    <ul className={styles.noticeItems}>
                      {notice.items.map((item) => (
                        <li key={item.label}>
                          <span className={styles.noticeItemLabel}>{item.label}</span>
                          {item.detail !== undefined ? (
                            <span className={styles.noticeDetail}> — {item.detail}</span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {showMergedResults ? (
          <CompareSummaryTable
            items={summarySeries}
            onOpenRun={openRunByKey}
            includeSampleCount={config.includeSampleCount}
            exportFilename={exportFilename}
            exportAppendix={exportAppendix}
          />
        ) : null}

        {mode === "compare" && compareRuns.length > 0 ? (
          <IsaacEvalComparison controller={controller} onOpenRun={openRunByKey} />
        ) : null}

        {mergedResult !== null ? <SeedStabilityTable result={mergedResult} /> : null}

        {compareRuns.length > 0 ? (
          <Section title="Policy">
            <PolicyCompareTable
              items={configurationRuns}
              onOpenRun={openRunByKey}
              showRunSublabels={config.showRunSublabels}
            />
          </Section>
        ) : null}

        {compareRuns.length > 0 && hasScenarioData ? (
          <Section title="Scenario 요약">
            <ScenarioCompareTable
              items={configurationRuns}
              onOpenRun={openRunByKey}
              showRunSublabels={config.showRunSublabels}
            />
          </Section>
        ) : null}

        {showMergedResults && hasHeadlineMetrics ? (
          <Section title="주요 지표">
            <KpiCompareChart items={resultSeries} />
          </Section>
        ) : null}

        {showMergedResults && hasDistributions ? (
          <Section title="분포">
            <DistributionCompareChart items={resultSeries} />
          </Section>
        ) : null}

        {showMergedResults && hasOutcomes ? (
          <Section title="Episode 결과">
            <OutcomeCompareChart items={resultSeries} />
            {baselineSeries !== null ? (
              <p className={styles.sectionNote}>
                Baseline은 Seed {baselineSeries.baseline.seedCount}개를 병합한 결과라 Episode 수가
                사용자 실행보다 많습니다. 막대 높이는 전체 대비 비율로 그렸고, 숫자는 실제 Episode
                수입니다.
              </p>
            ) : null}
          </Section>
        ) : null}

        {config.showDurations ? (
          <Section title="소요 시간">
            <DurationCompareChart items={configurationRuns} />
            {baselineSeries !== null ? (
              <p className={styles.sectionNote}>Baseline 소요 시간은 Seed별 실행의 중앙값입니다.</p>
            ) : null}
          </Section>
        ) : null}
      </div>
    </article>
  );
}

function SubtitleLoading({ children }: { children: ReactNode }): ReactNode {
  return (
    <span className={`${inlineLoadingLineClassName} ${styles.subtitleLoading}`}>
      ·
      <Spinner size="sm" className={inlineSpinnerClassName} />
      {children}
    </span>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): ReactNode {
  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      {children}
    </section>
  );
}

function openBaselineSession(baseline: NonNullable<EvalResultSeries["baseline"]>): () => void {
  return () =>
    openTrainingSessionWindow(
      sessionReference(baseline.sessionId, baseline.sessionName ?? "Baseline"),
    );
}

function LegendChip({
  item,
  onOpen,
  openTitle = "이 실행의 상세 창을 엽니다.",
}: {
  item: EvalResultSeries;
  onOpen?: () => void;
  openTitle?: string;
}): ReactNode {
  const content = (
    <>
      <SeriesSwatch item={item} />
      <span>{item.label}</span>
      <span className={styles.chipSub}>{item.sublabel}</span>
    </>
  );
  if (onOpen === undefined) return <span className={styles.chip}>{content}</span>;
  return (
    <button type="button" className={styles.chip} title={openTitle} onClick={onOpen}>
      {content}
    </button>
  );
}
